import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  useDeleteUser,
  useDisableUser,
  useEnableUser,
  useRevokeAllSessions,
  useRevokeSession,
  userDetailQuery,
} from '@/features/users/users.queries'
import { UserGroupsPanel } from '@/features/users/UserGroupsPanel'
import { UserLimitsPanel } from '@/features/users/UserLimitsPanel'
import { UserAnalyticsPanel } from '@/features/analytics/UserAnalyticsPanel'
import { PERMISSIONS, usePermission, usePermissions } from '@/features/auth/permissions'
import { formatBytes, formatDate, formatDateTime, formatRelative } from '@/lib/format'
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Copyable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  TabPanel,
  Tabs,
  TextAreaField,
  useToast,
} from '@/ui'
import { IconArrowBack, IconBlock, IconCheck, IconDelete, IconLogout } from '@/icons'

/* Key written out per tab rather than composed as `users.tab${id}`: a template literal is
   not a known key, so composing one would opt this file out of key checking entirely. */
const TABS = [
  { id: 'overview', labelKey: 'users.tabOverview' },
  { id: 'sessions', labelKey: 'users.tabSessions' },
  { id: 'groups', labelKey: 'users.tabGroups' },
  { id: 'limits', labelKey: 'users.tabLimits' },
  { id: 'worlds', labelKey: 'users.tabWorlds' },
  { id: 'moderation', labelKey: 'users.tabModeration' },
  { id: 'activity', labelKey: 'users.tabActivity' },
  { id: 'analytics', labelKey: 'users.tabAnalytics' },
] as const

type TabId = (typeof TABS)[number]['id']
const TAB_IDS = TABS.map((tab) => tab.id) as unknown as [TabId, ...TabId[]]

const searchSchema = z.object({
  tab: z.enum(TAB_IDS).default('overview'),
})

export const Route = createFileRoute('/_authed/users/$userId')({
  validateSearch: searchSchema,
  component: UserDetailPage,
})

function UserDetailPage() {
  const { t, i18n } = useTranslation()
  const { userId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const toast = useToast()

  const { identity } = usePermissions()
  const canDisable = usePermission(PERMISSIONS.USER_DISABLE)
  const canReadAnalytics = usePermission(PERMISSIONS.ANALYTICS_READ)

  const query = useQuery(userDetailQuery(userId))
  const disable = useDisableUser()
  const enable = useEnableUser()
  const revokeSession = useRevokeSession()
  const revokeAll = useRevokeAllSessions()
  const remove = useDeleteUser()

  const [confirming, setConfirming] = useState<
    'disable' | 'enable' | 'revokeAll' | 'delete' | null
  >(null)
  const [reason, setReason] = useState('')

  if (query.isPending) return <LoadingState />
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  const user = query.data
  // The server refuses this too; the control is hidden so nobody discovers it by being refused.
  const isSelf = identity?.id === user.id

  // The drill-down is the most sensitive read in the feature, so the tab is not offered to an
  // admin who could not load it — a tab that always errors teaches people to ignore errors.
  const tabs = TABS.filter((tab) => tab.id !== 'analytics' || canReadAnalytics).map((tab) => ({
    id: tab.id,
    label: t(tab.labelKey),
    ...(tab.id === 'sessions' ? { count: user.sessions.length } : {}),
    ...(tab.id === 'groups' ? { count: user.roles.length } : {}),
    ...(tab.id === 'worlds' ? { count: user.worlds.length } : {}),
  }))

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to="/users"
            search={{ q: '', status: 'all', sort: 'createdAt', order: 'desc' }}
            className="mb-2 inline-flex items-center gap-1 text-[0.8125rem] text-text-secondary hover:text-text"
          >
            <IconArrowBack size={14} />
            {t('users.title')}
          </Link>
        }
        title={user.displayName}
        description={user.email}
        actions={
          canDisable && !isSelf ? (
            <>
              <Button
                variant="secondary"
                icon={<IconLogout size={15} />}
                onClick={() => {
                  setConfirming('revokeAll')
                }}
              >
                {t('users.revokeAll')}
              </Button>
              {user.isDisabled ? (
                <Button
                  variant="primary"
                  icon={<IconCheck size={15} />}
                  onClick={() => {
                    setConfirming('enable')
                  }}
                >
                  {t('users.enable')}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  icon={<IconBlock size={15} />}
                  onClick={() => {
                    setConfirming('disable')
                  }}
                >
                  {t('users.disable')}
                </Button>
              )}

              <ActionMenu
                actions={[
                  {
                    id: 'delete',
                    label: t('users.deleteAccount'),
                    icon: <IconDelete size={15} />,
                    isDestructive: true,
                    isDisabled: user.isDeleted,
                    onSelect: () => {
                      setConfirming('delete')
                    },
                  },
                ]}
              />
            </>
          ) : undefined
        }
      />

      {isSelf ? (
        <p className="mb-4 rounded-control border border-info/25 bg-info-subtle px-3 py-2 text-[0.8125rem] text-info">
          {t('users.cannotDisableSelf')}
        </p>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Avatar src={user.avatarUrl} name={user.displayName} size={44} />
        {user.isDeleted ? (
          <Badge tone="danger" hasDot>
            {t('users.badgeDeleted')}
          </Badge>
        ) : user.isDisabled ? (
          <Badge tone="warning" hasDot>
            {t('users.badgeDisabled')}
          </Badge>
        ) : (
          <Badge tone="success" hasDot>
            {t('users.badgeActive')}
          </Badge>
        )}
        <Copyable value={user.id} />
      </div>

      <Tabs
        tabs={tabs}
        activeId={search.tab}
        onChange={(tab) => {
          void navigate({ search: { tab: tab as TabId } })
        }}
        className="mb-4"
      />

      <TabPanel id={search.tab}>
        {search.tab === 'overview' ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title={t('users.identities')} as="h3" />
              <dl className="mt-3 flex flex-col gap-2 text-[0.8125rem]">
                <Row label={t('users.columnJoined')} value={formatDate(user.createdAt, i18n.language)} />
                <Row
                  label={t('users.columnLastSeen')}
                  value={
                    user.lastSeenAt ? formatRelative(user.lastSeenAt, i18n.language) : t('common.never')
                  }
                />
                {user.identities.map((identityLink) => (
                  <Row
                    key={identityLink.provider}
                    label={identityLink.provider}
                    value={t('users.identityLinked', {
                      date: formatDate(identityLink.linkedAt, i18n.language),
                    })}
                  />
                ))}
              </dl>
            </Card>

            <Card>
              <CardHeader title={t('users.storageBreakdown')} as="h3" />
              {user.storage.length === 0 ? (
                <p className="mt-3 text-[0.8125rem] text-text-secondary">{t('common.notSet')}</p>
              ) : (
                <dl className="mt-3 flex flex-col gap-2 text-[0.8125rem]">
                  {user.storage.map((entry) => (
                    <Row
                      key={entry.type}
                      label={t(`assets.type${entry.type.toUpperCase()}`, { defaultValue: entry.type })}
                      value={`${formatBytes(entry.bytes, i18n.language)} · ${String(entry.count)}`}
                    />
                  ))}
                </dl>
              )}
            </Card>
          </div>
        ) : null}

        {search.tab === 'sessions' ? (
          <Card isFlush>
            <div className="p-5 pb-3">
              <CardHeader title={t('users.sessions')} as="h3" />
            </div>
            {user.sessions.length === 0 ? (
              <EmptyState title={t('users.sessionsEmpty')} />
            ) : (
              <ul className="divide-y divide-border">
                {user.sessions.map((session) => (
                  <li key={session.id} className="flex items-center gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[0.8125rem] font-medium text-text">
                        {session.deviceName ?? session.userAgent ?? t('common.unknown')}
                      </p>
                      <p className="truncate text-xs text-text-secondary">
                        {session.ipAddress ?? t('common.unknown')} ·{' '}
                        {formatRelative(session.lastSeenAt, i18n.language)}
                      </p>
                    </div>
                    {canDisable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        isLoading={revokeSession.isPending}
                        onClick={() => {
                          revokeSession.mutate(
                            { userId, sessionId: session.id },
                            {
                              onSuccess: () => {
                                toast.success(t('users.sessionRevoked'))
                              },
                              onError: (error) => {
                                toast.fromError(error, t('state.errorTitle'))
                              },
                            },
                          )
                        }}
                      >
                        {t('users.revokeSession')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {search.tab === 'groups' ? (
          <UserGroupsPanel userId={userId} displayName={user.displayName} roles={user.roles} />
        ) : null}

        {search.tab === 'limits' ? (
          <UserLimitsPanel userId={userId} parameters={user.parameters} />
        ) : null}

        {search.tab === 'worlds' ? (
          <Card isFlush>
            <div className="p-5 pb-3">
              <CardHeader title={t('users.ownedWorlds')} as="h3" />
            </div>
            {user.worlds.length === 0 ? (
              <EmptyState title={t('users.noWorlds')} />
            ) : (
              <ul className="divide-y divide-border">
                {user.worlds.map((world) => (
                  <li key={world.slug}>
                    <Link
                      to="/worlds/$slug"
                      params={{ slug: world.slug }}
                      search={{ tab: 'overview' }}
                      className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-sunken"
                    >
                      <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-text">
                        {world.title}
                      </span>
                      <Badge>{world.status}</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {search.tab === 'moderation' ? (
          <Card isFlush>
            <div className="p-5 pb-3">
              <CardHeader title={t('users.moderationAgainst')} as="h3" />
            </div>
            {user.moderationAgainst.length === 0 ? (
              <EmptyState title={t('users.noModeration')} />
            ) : (
              <ul className="divide-y divide-border">
                {user.moderationAgainst.map((action) => (
                  <li key={action.id} className="flex items-center gap-3 px-5 py-3">
                    <Badge tone="warning">
                      {t(`moderation.type${action.action}`, { defaultValue: action.action })}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[0.8125rem] text-text">
                        {action.worldTitle ?? t('common.notSet')}
                      </p>
                      {action.reason ? (
                        <p className="truncate text-xs text-text-secondary">{action.reason}</p>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-xs text-text-secondary">
                      {formatRelative(action.createdAt, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {search.tab === 'activity' ? (
          <Card isFlush>
            <div className="p-5 pb-3">
              <CardHeader title={t('users.recentLogins')} as="h3" />
            </div>
            {user.recentLogins.length === 0 ? (
              <EmptyState title={t('users.noLogins')} />
            ) : (
              <ul className="divide-y divide-border">
                {user.recentLogins.map((attempt, index) => (
                  <li key={`${attempt.at}-${String(index)}`} className="flex items-center gap-3 px-5 py-3">
                    <Badge tone={attempt.success ? 'success' : 'danger'} hasDot>
                      {attempt.success ? t('users.loginSuccess') : t('users.loginFailed')}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
                      {/* Always null by design: login_attempts stores only a hash of the
                          address, so the table cannot be mined for valid accounts. */}
                      {attempt.ip ?? t('common.unknown')}
                    </span>
                    <span className="shrink-0 text-xs text-text-secondary">
                      {formatDateTime(attempt.at, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {search.tab === 'analytics' && canReadAnalytics ? (
          <UserAnalyticsPanel userId={userId} />
        ) : null}
      </TabPanel>

      <ConfirmDialog
        isOpen={confirming === 'disable'}
        onClose={() => {
          setConfirming(null)
        }}
        onConfirm={() => {
          disable.mutate(
            { userId, ...(reason ? { reason } : {}) },
            {
              onSuccess: () => {
                toast.success(t('users.disabled'))
                setConfirming(null)
                setReason('')
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('users.disableConfirm')}
        body={t('users.disableBody', { name: user.displayName })}
        confirmLabel={t('users.disable')}
        cancelLabel={t('common.cancel')}
        isPending={disable.isPending}
      >
        <TextAreaField
          label={t('users.disableReason')}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value)
          }}
        />
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={confirming === 'enable'}
        onClose={() => {
          setConfirming(null)
        }}
        onConfirm={() => {
          enable.mutate(
            { userId },
            {
              onSuccess: () => {
                toast.success(t('users.enabled'))
                setConfirming(null)
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('users.enableConfirm')}
        body={t('users.enableBody', { name: user.displayName })}
        confirmLabel={t('users.enable')}
        cancelLabel={t('common.cancel')}
        isDestructive={false}
        isPending={enable.isPending}
      />

      <ConfirmDialog
        isOpen={confirming === 'delete'}
        onClose={() => {
          setConfirming(null)
        }}
        onConfirm={() => {
          remove.mutate(
            { userId },
            {
              onSuccess: () => {
                toast.success(t('users.deleted'))
                setConfirming(null)
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('users.deleteConfirm')}
        body={t('users.deleteBody', { name: user.displayName })}
        confirmLabel={t('users.deleteAccount')}
        cancelLabel={t('common.cancel')}
        isPending={remove.isPending}
      />

      <ConfirmDialog
        isOpen={confirming === 'revokeAll'}
        onClose={() => {
          setConfirming(null)
        }}
        onConfirm={() => {
          revokeAll.mutate(
            { userId },
            {
              onSuccess: () => {
                toast.success(t('users.allSessionsRevoked'))
                setConfirming(null)
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('users.revokeAllConfirm')}
        body={t('users.revokeAllBody', { name: user.displayName })}
        confirmLabel={t('users.revokeAll')}
        cancelLabel={t('common.cancel')}
        isPending={revokeAll.isPending}
      />
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-text-secondary">{label}</dt>
      <dd className="min-w-0 truncate text-right text-text">{value}</dd>
    </div>
  )
}
